<template>
  <!-- Dieser div wird als Container für die Three.js-Szene verwendet. -->
  <div ref="container" class="three-container"></div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

import {getAllUsersByBalance, resolveApiUrl} from '../services/api';
import {DEFAULT_PROFILE_IMAGE} from "@/assets/config";

// Ref für den DOM-Container
const container = ref(null)

let scene
let camera
let renderer
let controls
let animationFrame
let clock

// URL für das Hintergrundbild der Szene
const WALL_IMAGE_URL = 'https://freizeitmonster.de/wp-content/uploads/kurfuerstliches-schloss-sehenswuerdigkeiten-bonn.jpg'

onMounted(async () => {
  init()
  // Baut die Szene asynchron auf, basierend auf den Daten aus der API.
  await buildSceneFromIterator()
  animate()

  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  // Räumt die Three.js-Ressourcen auf, um Speicherlecks zu vermeiden.
  cancelAnimationFrame(animationFrame)
  window.removeEventListener('resize', onResize)

  controls?.dispose()
  renderer?.dispose()
  renderer?.domElement?.remove()
})

/**
 * Ein asynchroner Generator, der die Benutzerdaten von der API abruft
 * und sie nacheinander als aufbereitete Objekte zurückgibt.
 */
async function* getUsers() {
  const users = await getAllUsersByBalance();
  console.log("Benutzer Abgerufen:", users);
  for (const user of users) {
    yield {
      id: user?.id,
      name: user?.nickname || user?.email,
      amount: user?.balance,
      avatarUrl: resolveApiUrl(user?.avatarUrl || user?.profileimage || DEFAULT_PROFILE_IMAGE)
    };
  }
}

/**
 * Iteriert über den `getUsers`-Generator und erstellt für jeden Benutzer einen 3D-Block.
 */
async function buildSceneFromIterator() {
  const users = []

  for await (const user of getUsers()) {
    users.push(user)
    createUserBlock(user, users.length - 1)
  }
}

/**
 * Initialisiert die grundlegende Three.js-Szene (Kamera, Renderer, Lichter, Steuerung).
 */
function init() {
  scene = new THREE.Scene()
  scene.background = new THREE.Color(0x081634)
  scene.fog = new THREE.Fog(0x081634, 40, 180)

  const width = container.value.clientWidth
  const height = container.value.clientHeight

  camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
  camera.position.set(0, 16, 32)

  // Initialisiert den WebGL-Renderer.
  renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setSize(width, height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  container.value.appendChild(renderer.domElement)

  controls = new OrbitControls(camera, renderer.domElement)
  // Konfiguriert die Kamera-Steuerung für ein angenehmes Benutzererlebnis.
  controls.enableDamping = true
  controls.dampingFactor = 0.08
  controls.enablePan = false
  controls.minDistance = 8
  controls.maxDistance = 80
  controls.maxPolarAngle = Math.PI / 2.02
  controls.target.set(0, 12, 0)
  controls.update()

  clock = new THREE.Clock()

  // Fügt verschiedene Lichtquellen hinzu, um die Szene auszuleuchten.
  const ambientLight = new THREE.AmbientLight(0xffffff, 1.15)
  scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xffffff, 2.35)
  directionalLight.position.set(8, 20, 12)
  scene.add(directionalLight)

  const hemiLight = new THREE.HemisphereLight(0x081634, 0x4f9a3d, 1.2)
  scene.add(hemiLight)

  // Erstellt eine einfache grüne Bodenebene.
  const groundGeometry = new THREE.PlaneGeometry(120, 120)
  const groundMaterial = new THREE.MeshStandardMaterial({ color: 0x4f9a3d, roughness: 1 })
  const ground = new THREE.Mesh(groundGeometry, groundMaterial)
  ground.rotation.x = -Math.PI / 2
  ground.position.y = -0.01
  scene.add(ground)

  createBackWall();
}

/**
 * Erstellt eine Rückwand und lädt ein Hintergrundbild darauf.
 */
function createBackWall() {
  const wallHeight = 42
  const wallWidth = 120
  const wallZ = -3

  const wallGeometry = new THREE.PlaneGeometry(wallWidth, wallHeight)
  const wallMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 })
  const wall = new THREE.Mesh(wallGeometry, wallMaterial)
  wall.position.set(0, wallHeight / 2 - 0.01, wallZ)
  scene.add(wall)

  const loader = new THREE.TextureLoader()
  loader.load(
    WALL_IMAGE_URL,
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace
      wall.material = new THREE.MeshStandardMaterial({ map: texture, roughness: 1 })
      wall.material.needsUpdate = true
    },
    undefined,
    () => {
      wall.material = new THREE.MeshStandardMaterial({ color: 0xdbeafe, roughness: 1 })
      wall.material.needsUpdate = true
    }
  )
}

/**
 * Erstellt einen einzelnen 3D-Block für einen Benutzer auf dem Podest.
 * @param {object} user - Das Benutzerobjekt.
 * @param {number} index - Der Index des Benutzers in der Rangliste.
 */
function createUserBlock(user, index) {
  const scale = Math.pow(0.75, index)
  const width = 7
  const depth = 7
  const height = 15 * scale
  const spacing = 9.0
  const x = index * spacing - ((3) * spacing) / 2
  const y = height / 2
  const z = 6.5 + index * 0.2

  const boxMaterial = new THREE.MeshStandardMaterial({
    color: 0xff2b2b,
    roughness: 0.5,
    metalness: 0.02
  });
  const box = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), boxMaterial)
  box.position.set(x, y, z)
  scene.add(box);
  addAmountTextToBox(box, width, height, depth, user.amount)
  addBillboardAboveBox(box, user)
}

/**
 * Fügt den formatierten Geldbetrag als Textur auf die Seiten des 3D-Blocks.
 * @param {THREE.Mesh} box - Der 3D-Block.
 * @param {number} width - Die Breite des Blocks.
 * @param {number} height - Die Höhe des Blocks.
 * @param {number} depth - Die Tiefe des Blocks.
 * @param {number} amount - Der Geldbetrag.
 */
function addAmountTextToBox(box, width, height, depth, amount) {
  const text = `€ ${formatMoney(amount)}`

  const textureInfo = makeTextTexture(text, {
    fontSize: 180,
    color: '#1d4ed8',
    background: 'rgba(255,255,255,0)'
  })

  const texture = textureInfo.texture

  const faces = [
    { pos: [0, 0, depth / 2 + 0.03], rot: [0, 0, 0] },
    { pos: [0, 0, -depth / 2 - 0.03], rot: [0, Math.PI, 0] },
    { pos: [width / 2 + 0.03, 0, 0], rot: [0, -Math.PI / 2, 0] },
    { pos: [-width / 2 - 0.03, 0, 0], rot: [0, Math.PI / 2, 0] },
    { pos: [0, height / 2 + 0.03, 0], rot: [-Math.PI / 2, 0, 0] },
    { pos: [0, -height / 2 - 0.03, 0], rot: [Math.PI / 2, 0, 0] }
  ]

  faces.forEach((face) => {
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false
    })

    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(width * 0.93, height * 0.72),
      material
    )

    plane.position.set(...face.pos)
    plane.rotation.set(...face.rot)

    box.add(plane)
  })
}

/**
 * Erstellt ein "Billboard" (Avatar und Name), das über dem 3D-Block schwebt.
 * @param {THREE.Mesh} box - Der 3D-Block.
 * @param {object} user - Das Benutzerobjekt.
 */
function addBillboardAboveBox(box, user) {
  const group = new THREE.Group()
  group.position.set(0, box.geometry.parameters.height / 2 + 2.4, 0)
  box.add(group)

  const avatarSize = 2.0
  const avatarTexture = makeCircularAvatarTexture(user.avatarUrl)
  const avatarMaterial = new THREE.MeshBasicMaterial({
    map: avatarTexture,
    transparent: true,
    depthWrite: false
  })
  const avatar = new THREE.Mesh(new THREE.CircleGeometry(avatarSize / 2, 64), avatarMaterial)
  avatar.position.set(0, 0, 0)
  group.add(avatar)

  const nameTextureInfo = makeTextTexture(user.name, {
    fontSize: 120,
    color: '#0f172a',
    background: 'rgba(255,255,255,0)'
  })

  const nameMaterial = new THREE.MeshBasicMaterial({
    map: nameTextureInfo.texture,
    transparent: true,
    depthWrite: false
  })

  const nameScale = 0.007
  const namePlane = new THREE.Mesh(
    new THREE.PlaneGeometry(nameTextureInfo.width * nameScale, nameTextureInfo.height * nameScale),
    nameMaterial
  )
  namePlane.position.set(0, 1.95, 0)
  group.add(namePlane)

  box.userData.billboardGroup = group
}

/**
 * Erstellt eine runde Avatar-Textur aus einer Bild-URL.
 * @param {string} imageUrl - Die URL des Avatar-Bildes.
 * @returns {THREE.CanvasTexture}
 */
function makeCircularAvatarTexture(imageUrl) {
  const size = 512
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = imageUrl

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace

  img.onload = () => {
    ctx.clearRect(0, 0, size, size)
    ctx.save()
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()

    const scale = Math.max(size / img.width, size / img.height)
    const drawWidth = img.width * scale
    const drawHeight = img.height * scale
    const dx = (size - drawWidth) / 2
    const dy = (size - drawHeight) / 2
    ctx.drawImage(img, dx, dy, drawWidth, drawHeight)
    ctx.restore()
    texture.needsUpdate = true
  }

  img.onerror = () => {
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = '#e5e7eb'
    ctx.fillRect(0, 0, size, size)
    ctx.fillStyle = '#64748b'
    ctx.font = 'bold 180px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('?', size / 2, size / 2)
    texture.needsUpdate = true
  }

  return texture
}

/**
 * Erstellt eine Textur aus einem Text-String.
 * @param {string} text - Der zu rendernde Text.
 * @param {object} options - Styling-Optionen.
 * @returns {{texture: THREE.CanvasTexture, width: number, height: number}}
 */
function makeTextTexture(text, options = {}) {
  const fontSize = options.fontSize || 120
  const paddingX = options.paddingX || fontSize * 1.2
  const paddingY = options.paddingY || fontSize * 0.85

  const measureCanvas = document.createElement('canvas')
  const measureCtx = measureCanvas.getContext('2d')
  measureCtx.font = `bold ${fontSize}px Arial, sans-serif`
  const measuredWidth = Math.ceil(measureCtx.measureText(text).width)

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(512, measuredWidth + paddingX * 2)
  canvas.height = Math.max(256, Math.ceil(fontSize + paddingY * 2))
  const ctx = canvas.getContext('2d')

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = options.background || 'rgba(255,255,255,0)'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  ctx.font = `bold ${fontSize}px Arial, sans-serif`
  ctx.fillStyle = options.color || '#1d4ed8'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.lineWidth = Math.max(6, Math.round(fontSize * 0.08))
  ctx.strokeStyle = 'rgba(255,255,255,0.96)'
  ctx.strokeText(text, canvas.width / 2, canvas.height / 2)
  ctx.fillText(text, canvas.width / 2, canvas.height / 2)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true

  return {
    texture,
    width: canvas.width,
    height: canvas.height
  }
}

/**
 * Formatiert eine Zahl als deutschen Währungsstring.
 * @param {number} value
 */
function formatMoney(value) {
  return new Intl.NumberFormat('de-DE').format(value)
}

/**
 * Die Haupt-Animationsschleife, die bei jedem Frame aufgerufen wird.
 */
function animate() {
  animationFrame = requestAnimationFrame(animate)

  const elapsed = clock.getElapsedTime()

  // Aktualisiert die Billboards, damit sie immer zur Kamera zeigen.
  scene.traverse((obj) => {
    if (obj.userData?.billboardGroup) {
      obj.userData.billboardGroup.quaternion.copy(camera.quaternion)
      obj.userData.billboardGroup.position.y = obj.geometry.parameters.height / 2 + 2.4 + Math.sin(elapsed * 2 + obj.position.x) * 0.03
    }
  });

  controls.update()
  renderer.render(scene, camera)
}

/**
 * Handler, der aufgerufen wird, wenn die Fenstergröße geändert wird, um die Szene responsiv zu halten.
 */
function onResize() {
  const width = container.value.clientWidth
  const height = container.value.clientHeight

  camera.aspect = width / height
  camera.updateProjectionMatrix()

  renderer.setSize(width, height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
}
</script>

<style scoped>
.three-container {
  width: 100%;
  height: 100vh;
}
.three-iframe {
  position: absolute;
  width: 400px;
  height: 600px;
  border: none;
  pointer-events: auto; /* wichtig: klickbar */
  transform-origin: 0 0;
  z-index: 10;
}
</style>
